import React, { useEffect, useState, useMemo } from 'react';
import { Box, Typography, TextField, CircularProgress, Button } from '@mui/material';
import { getCarriers } from '../../../pages/PurchaseInvoice/api/api';
import { CarrierOptions } from './type';

type Props = {
  selectedCarrier: string;
  onChange: (id: string) => void;
  onNext: () => void;
};

export default function Step1ChooseCarrier({ selectedCarrier, onChange }: Props) {
  const [carrierOptions, setCarrierOptions] = useState<CarrierOptions[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  // Fetch all carriers once
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getCarriers();
        const mappedCarriersOptions = data?.map((c: { _id: string; name: string; code: string; logo: string }) => ({
          value: c._id,
          display: c.name,
          // image: c.logo,
        }));
        setCarrierOptions(mappedCarriersOptions);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filter carriers based on search input
  const filteredCarriers = useMemo(() => {
    return carrierOptions.filter(c => c.display?.toLowerCase().includes(search.toLowerCase()));
  }, [carrierOptions, search]);

  return (
    <Box>
      {/* Search Field */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" gap={2}>
          <TextField
            placeholder="Search carriers..."
            variant="outlined"
            size="small"
            sx={{ flex: 1, minWidth: '400px' }}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </Box>
      </Box>

      <Box my="2rem" pb="0.5rem" borderBottom="4px solid #080808" width="220px">
        <Typography className="md-text" fontWeight={700}>
          Select Unbilled Carriers
        </Typography>
      </Box>

      {/* Carrier List */}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {filteredCarriers.length > 0 ? (
            filteredCarriers.map(carrier => (
              <Box key={carrier.value}>
                <Box
                  display="grid"
                  gridTemplateColumns="1fr 1fr"
                  alignItems="center"
                  border="1px solid #e0e0e0"
                  margin="10px 0"
                  borderRadius={3}
                  p={2}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f9f9f9' } }}
                  onClick={() => onChange(carrier.value)}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        backgroundColor: '#f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        border: '1px solid #ddd',
                      }}
                    >
                      {carrier?.image ? (
                        <img
                          src={carrier?.image}
                          alt={carrier.display}
                          style={{
                            width: '100%',
                            height: '100%',
                            borderRadius: '50%',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        carrier.display?.charAt(0)?.toUpperCase() || 'C'
                      )}
                    </Box>
                    <Typography fontWeight="bold">{carrier.display?.toUpperCase()}</Typography>
                  </Box>

                  <Box display="flex" justifyContent="flex-end" alignItems="center" gap={3}>
                    <Button
                      variant={selectedCarrier === carrier.value ? 'contained' : 'outlined'}
                      sx={{
                        borderRadius: 5,
                        px: 2.5,
                        py: 1,
                        fontSize: '14px',
                        minWidth: 100,
                        fontWeight: '600',
                      }}
                    >
                      {selectedCarrier === carrier.value ? 'Selected' : 'Select'}
                    </Button>
                  </Box>
                </Box>
              </Box>
            ))
          ) : (
            <Typography textAlign="center" mt={2} color="text.secondary">
              No carriers found.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
